import { useState, useEffect } from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';
import Modal from '@components/ui/Modal';

/**
 * FinancialsDateRangeModal
 *
 * The Financials page's Custom-date entry point. Left rail lists quick
 * presets (Today / Last N days / MTD / YTD / All time); right side is a
 * dual-month calendar range picker (react-day-picker `mode="range"`).
 * Bottom bar echoes the applied range and confirms with "Set Date Range".
 *
 * The parent owns the applied state — this component is fully controlled
 * via `initialRange` + `initialPresetId` (seeded on each open) and
 * `onApply(presetId, range)` on confirm. Because presets like `last30` /
 * `last12mo` / `all` map 1:1 to the toolbar's Monthly / Yearly / All tabs,
 * the parent can flip those tabs from the returned presetId to keep the
 * two surfaces in sync.
 *
 * Anything the user typed on the calendar that doesn't match a preset
 * returns as `presetId: 'custom'`, and the parent flips the toolbar into
 * the Custom pill.
 */

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

// Preset definitions. `resolve()` returns { from, to } or null (null = "no
// bounds", used only by All time). The date math intentionally mirrors the
// toolbar's Monthly/Yearly semantics (past 30 / past 365 days from today)
// so Monthly-pill and "Last 30 days"-preset resolve to identical requests.
const PRESETS = [
  { id: 'today', label: 'Today', resolve: () => {
    const today = startOfDay(new Date());
    return { from: today, to: today };
  } },
  { id: 'last7', label: 'Last 7 days', resolve: () => {
    const to = startOfDay(new Date());
    const from = new Date(to);
    from.setDate(from.getDate() - 6); // inclusive of today
    return { from, to };
  } },
  { id: 'last30', label: 'Last 30 days', resolve: () => {
    const to = startOfDay(new Date());
    const from = new Date(to);
    from.setDate(from.getDate() - 29);
    return { from, to };
  } },
  { id: 'last3mo', label: 'Last 3 months', resolve: () => {
    const to = startOfDay(new Date());
    const from = new Date(to);
    from.setMonth(from.getMonth() - 3);
    return { from, to };
  } },
  { id: 'last12mo', label: 'Last 12 months', resolve: () => {
    const to = startOfDay(new Date());
    const from = new Date(to);
    from.setFullYear(from.getFullYear() - 1);
    return { from, to };
  } },
  { id: 'mtd', label: 'Month to date', resolve: () => {
    const to = startOfDay(new Date());
    const from = new Date(to.getFullYear(), to.getMonth(), 1);
    return { from, to };
  } },
  { id: 'ytd', label: 'Year to date', resolve: () => {
    const to = startOfDay(new Date());
    const from = new Date(to.getFullYear(), 0, 1);
    return { from, to };
  } },
  { id: 'all', label: 'All time', resolve: () => null },
];

const formatDateShort = (d) => {
  if (!d) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const FinancialsDateRangeModal = ({
  isOpen,
  onClose,
  initialRange = null,
  initialPresetId = null,
  onApply,
}) => {
  // DayPicker's range shape: { from: Date, to: Date | undefined } | undefined
  const [range, setRange] = useState(initialRange || undefined);
  const [presetId, setPresetId] = useState(initialPresetId || null);

  // Re-seed on every open so the modal reflects whatever the toolbar
  // currently has applied. If the parent passes a preset id (e.g. 'last30'
  // from the Monthly tab), resolve it to concrete dates so the calendar
  // shows the highlighted fill on open — otherwise the sidebar highlights
  // a preset but the calendar looks empty until the user clicks it again.
  useEffect(() => {
    if (!isOpen) return;
    if (initialPresetId) {
      const preset = PRESETS.find((p) => p.id === initialPresetId);
      if (preset) {
        setRange(preset.resolve() || undefined);
        setPresetId(initialPresetId);
        return;
      }
    }
    setRange(initialRange || undefined);
    setPresetId(initialPresetId || null);
  }, [isOpen, initialRange, initialPresetId]);

  const handlePresetClick = (preset) => {
    setPresetId(preset.id);
    const resolved = preset.resolve();
    setRange(resolved || undefined);
  };

  const handleCalendarChange = (nextRange) => {
    setRange(nextRange);
    // A manual click on the calendar always breaks the preset highlight —
    // the user is now picking their own range.
    setPresetId(null);
  };

  // Enable Apply only when there's something meaningful to commit.
  const canApply = presetId === 'all' || (range?.from && range?.to);

  const handleApply = () => {
    if (!canApply) return;
    if (presetId === 'all') {
      onApply('all', null);
      return;
    }
    onApply(presetId || 'custom', { from: range.from, to: range.to || range.from });
  };

  const rangeLabel = presetId === 'all'
    ? 'All time'
    : (range?.from && range?.to
        ? `${formatDateShort(range.from)}  →  ${formatDateShort(range.to)}`
        : (range?.from ? `${formatDateShort(range.from)}  →  …` : '—'));

  // Anchor the two-month view so the applied `to` date is visible on load
  // (default DayPicker anchor is today, which hides older ranges).
  const defaultMonth = range?.from || new Date();

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" title="Pick a date range">
      <div className="px-6 pt-4 pb-6">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Left rail — preset shortcuts */}
          <div className="flex md:flex-col gap-1 md:w-40 shrink-0 flex-wrap md:flex-nowrap">
            {PRESETS.map((preset) => {
              const active = presetId === preset.id;
              return (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => handlePresetClick(preset)}
                  className={`text-left px-4 py-2 rounded-lg text-sm transition-colors whitespace-nowrap ${
                    active
                      ? 'bg-gray-100 text-gray-900 font-medium'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {preset.label}
                </button>
              );
            })}
          </div>

          {/* Right — dual-month calendar */}
          <div className="flex-1 min-w-0 flex justify-center">
            <DayPicker
              mode="range"
              selected={range}
              onSelect={handleCalendarChange}
              numberOfMonths={2}
              defaultMonth={defaultMonth}
              weekStartsOn={1}
              className="rdp-financials"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mt-6 pt-4 border-t border-gray-100">
          <div className="text-sm">
            <span className="text-gray-500">Range: </span>
            <span className="font-medium text-gray-900">{rangeLabel}</span>
          </div>
          <div className="flex gap-2 self-end sm:self-auto">
            {/* Clear resets the picker to an empty state so the user can
                start over without cancelling out. Does not close the modal
                or write anything to the parent — Apply is still required. */}
            <button
              type="button"
              onClick={() => {
                setRange(undefined);
                setPresetId(null);
              }}
              disabled={!range && !presetId}
              className="px-4 py-2 text-sm text-gray-600 rounded-full hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 rounded-full hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApply}
              disabled={!canApply}
              className="px-6 py-2 text-sm text-white bg-primary-700 rounded-full hover:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Set Date Range
            </button>
          </div>
        </div>
      </div>

      {/* Scoped styling to match the reference image. react-day-picker v10
          exposes CSS vars for its default theme, plus BEM-ish class names
          per element we can target directly for the pill/range visuals. */}
      <style>{`
        .rdp-financials {
          --rdp-accent-color: rgb(29, 60, 106);
          --rdp-accent-background-color: rgb(219, 234, 254);
          --rdp-selected-border: 0;
          margin: 0;
        }
        .rdp-financials .rdp-months {
          gap: 24px;
        }
        .rdp-financials .rdp-month_caption {
          background: rgb(245, 245, 245);
          border-radius: 12px;
          padding: 8px 12px;
          margin-bottom: 12px;
          font-weight: 500;
          color: rgb(31, 41, 55);
        }
        .rdp-financials .rdp-nav {
          padding: 4px;
        }
        .rdp-financials .rdp-nav button {
          background: rgb(245, 245, 245);
          border-radius: 999px;
          width: 28px;
          height: 28px;
        }
        .rdp-financials .rdp-nav button:hover:not([disabled]) {
          background: rgb(229, 231, 235);
        }
        .rdp-financials .rdp-weekday {
          font-size: 11px;
          font-weight: 500;
          text-transform: uppercase;
          color: rgb(107, 114, 128);
          padding: 8px 0;
        }
        .rdp-financials .rdp-day {
          padding: 0;
        }
        .rdp-financials .rdp-day_button {
          border-radius: 999px;
          font-size: 13px;
          width: 36px;
          height: 36px;
          color: rgb(55, 65, 81);
        }
        .rdp-financials .rdp-day_button:hover:not([disabled]) {
          background: rgb(243, 244, 246);
        }
        .rdp-financials .rdp-outside .rdp-day_button {
          color: rgb(209, 213, 219);
        }
        /* Middle-of-range days: soft-fill background, no pill radius. */
        .rdp-financials .rdp-range_middle {
          background: rgb(219, 234, 254);
        }
        .rdp-financials .rdp-range_middle .rdp-day_button {
          background: transparent;
          border-radius: 0;
          color: rgb(29, 60, 106);
        }
        /* Range endpoints: solid dark navy pill. */
        .rdp-financials .rdp-range_start,
        .rdp-financials .rdp-range_end {
          background: rgb(219, 234, 254);
        }
        .rdp-financials .rdp-range_start {
          border-top-left-radius: 999px;
          border-bottom-left-radius: 999px;
        }
        .rdp-financials .rdp-range_end {
          border-top-right-radius: 999px;
          border-bottom-right-radius: 999px;
        }
        .rdp-financials .rdp-range_start .rdp-day_button,
        .rdp-financials .rdp-range_end .rdp-day_button {
          background: rgb(29, 60, 106);
          color: white;
        }
        /* Single-day range (from === to): full pill on both sides. */
        .rdp-financials .rdp-range_start.rdp-range_end {
          background: transparent;
          border-radius: 999px;
        }
        /* Today marker (only when not already selected). */
        .rdp-financials .rdp-today:not(.rdp-selected) .rdp-day_button {
          box-shadow: inset 0 0 0 1px rgb(219, 234, 254);
          color: rgb(29, 60, 106);
          font-weight: 600;
        }
      `}</style>
    </Modal>
  );
};

// Expose the preset list so the parent can map preset ids <-> its own tab
// state without duplicating the labels.
FinancialsDateRangeModal.PRESETS = PRESETS;

export default FinancialsDateRangeModal;
